import React, { useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import {
  Box,
  Button,
  Stack,
  Grid,
  Typography,
  Chip,
  Divider,
  CardContent,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, Save as SaveIcon, Delete as DeleteIcon, Person as PersonIcon, Group as GroupIcon, Info as InfoIcon } from '@mui/icons-material';

import { Container, Input, ErrorMessage, InfoLabel, InfoValue, SectionHeader, StyledCard, LoadingState, ErrorState, EmptyState, CustomSwitch } from '@/components/common';
import { useUserDetail, useUpdateUser, useDeleteUser, useUpdateUserStatus } from '@/hooks/users.hook';
import { successToast, errorToast, confirmToast } from '@/common/toast';
import { users } from '@/constants';
import { formatDate } from '@/util/dateUtils';
import { isActiveStatus, nextToggleStatus, USER_STATUS, userStatusChangeMessage, userStatusLabel } from '@/utils/userStatus';

/**
 * 교육생 상세 페이지 컴포넌트
 * 
 * 교육생의 상세 정보를 조회하고 수정할 수 있는 페이지입니다.
 */
const StudentDetailPage = () => {
  const { userId } = useParams();
  const navigate = useNavigate();

  // 교육생 상세 정보 조회
  const { data: detailData, isLoading, error } = useUserDetail(userId);

  // API 응답 구조: { user, group, groupCourseList }
  const userData = detailData;
  const groupData = detailData?.group;
  const groupCourseList = detailData?.groupCourseList || [];

  // 수정, 삭제, 상태 변경 훅
  const updateUserMutation = useUpdateUser();
  const deleteUserMutation = useDeleteUser();
  const updateStatusMutation = useUpdateUserStatus();

  // 폼 관리
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm({
    defaultValues: {
      userId: '',
      name: '',
      email: '',
      group: {
        groupId: '',
      },
    },
  });

  // 데이터 로드 시 폼에 값 설정
  useEffect(() => {
    if (userData) {
      reset({
        userId: userData.userId || '',
        name: userData.name || '',
        email: userData.email || '',
        group: {
          groupId: userData?.group?.groupId || '',
        },
      });
    }
  }, [userData, reset]);

  /**
   * 교육생 정보 수정 핸들러
   */
  const onSubmit = useCallback(
    (data) => {
      updateUserMutation.mutate(
        {
          userId: data.userId,
          name: data.name,
          email: data.email,
          groupId: data.group.groupId,
        },
        {
          onSuccess: () => {
            successToast('교육생 정보가 수정되었습니다.');
          },
          onError: (error) => {
            errorToast(`교육생 정보 수정에 실패했습니다. - ${error.response?.data?.msg}`);
          },
        }
      );
    },
    [updateUserMutation]
  );

  /**
   * 교육생 삭제 핸들러
   */
  const handleDelete = useCallback(() => {
    confirmToast('정말로 이 교육생을 삭제하시겠습니까?', () => {
      deleteUserMutation.mutate(userId, {
        onSuccess: () => {
          successToast('교육생이 삭제되었습니다.');
          navigate('/students');
        },
        onError: (error) => {
          errorToast(`교육생 삭제에 실패했습니다. - ${error.response?.data?.msg}`);
        },
      });
    });
  }, [userId, deleteUserMutation, navigate]);

  /**
   * 상태 변경 핸들러
   */
  const handleStatusChange = useCallback(() => {
    const newStatus = nextToggleStatus(userData?.status);

    updateStatusMutation.mutate(
      { userId, status: newStatus },
      {
        onSuccess: () => {
          successToast('교육생 상태가 변경되었습니다.');
        },
        onError: (error) => {
          errorToast(`상태 변경에 실패했습니다. - ${error.response?.data?.msg}`);
        },
      }
    );
  }, [userId, userData?.status, updateStatusMutation]);

  /**
   * 뒤로가기 핸들러
   */
  const handleGoBack = useCallback(() => {
    navigate('/students');
  }, [navigate]);

  // 로딩 중
  if (isLoading) {
    return <LoadingState title='교육생 상세' />;
  }

  // 에러 발생
  if (error) {
    return <ErrorState title='교육생 상세' message='교육생 정보를 불러오는 중 오류가 발생했습니다.' onGoBack={handleGoBack} />;
  }

  // 데이터 없음
  if (!userData) {
    return <EmptyState title='교육생 상세' message='교육생 정보를 찾을 수 없습니다.' onGoBack={handleGoBack} />;
  }


  return (
    <Container title='교육생 상세'>
      <Stack spacing={3}>
        {/* 상단 버튼 영역 */}
        <Box display='flex' justifyContent='space-between' alignItems='center' flexWrap='wrap' gap={2}>
          <Button 
            variant='outlined' 
            onClick={handleGoBack} 
            startIcon={<ArrowBackIcon />}
            sx={{ minWidth: 120 }}
          >
            목록으로
          </Button>
          <Stack direction='row' spacing={1}>
            <Button
              variant='outlined'
              color='error'
              onClick={handleDelete}
              startIcon={<DeleteIcon />}
              disabled={deleteUserMutation.isPending}
              sx={{ minWidth: 100 }}
            >
              삭제
            </Button>
            <Button
              variant='contained'
              onClick={handleSubmit(onSubmit)}
              startIcon={<SaveIcon />}
              disabled={!isDirty || updateUserMutation.isPending}
              sx={{ minWidth: 100 }}
            >
              저장
            </Button>
          </Stack>
        </Box>

        <Grid container spacing={3}>
          {/* 왼쪽: 교육생 기본 정보 */}
          <Grid item xs={12} md={8}>
            <StyledCard>
              <CardContent sx={{ p: 3 }}>
                <SectionHeader>
                  <PersonIcon sx={{ color: '#1976d2', fontSize: 28 }} />
                  <Typography variant='h5' fontWeight={600}>
                    교육생 정보
                  </Typography>
                </SectionHeader>

                <Grid container spacing={3}>
                  {/* 사용자 ID */}
                  <Grid item xs={12} sm={6}>
                    <InfoLabel>사용자 ID</InfoLabel>
                    <Input
                      control={control}
                      name='userId'
                      textFieldProps={{
                        fullWidth: true,
                        disabled: true,
                        variant: 'outlined',
                        sx: { '& .MuiInputBase-input': { fontWeight: 500 } }
                      }}
                    />
                  </Grid>

                  {/* 이름 */}
                  <Grid item xs={12} sm={6}>
                    <InfoLabel>이름 *</InfoLabel>
                    <Input
                      control={control}
                      name='name'
                      textFieldProps={{
                        fullWidth: true,
                        required: true,
                        variant: 'outlined',
                      }}
                      rules={{
                        required: '이름을 입력해주세요.',
                      }}
                    />
                    <ErrorMessage errors={errors} name='name' />
                  </Grid>

                  {/* 이메일 */}
                  <Grid item xs={12} sm={6}>
                    <InfoLabel>이메일 *</InfoLabel>
                    <Input
                      control={control}
                      name='email'
                      textFieldProps={{
                        fullWidth: true,
                        type: 'email',
                        required: true,
                        variant: 'outlined',
                      }}
                      rules={{
                        required: '이메일을 입력해주세요.',
                        pattern: {
                          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                          message: '올바른 이메일 형식이 아닙니다.',
                        },
                      }}
                    />
                    <ErrorMessage errors={errors} name='email' />
                  </Grid>

                  {/* 그룹 ID */}
                  <Grid item xs={12} sm={6}>
                    <InfoLabel>그룹 ID</InfoLabel>
                    <Input
                      control={control}
                      name='group.groupId'
                      textFieldProps={{
                        fullWidth: true,
                        type: 'number',
                        variant: 'outlined',
                      }}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </StyledCard>
          </Grid>

          {/* 오른쪽: 상태 및 권한 정보 */}
          <Grid item xs={12} md={4}>
            <StyledCard>
              <CardContent sx={{ p: 3 }}>
                <SectionHeader>
                  <InfoIcon sx={{ color: '#1976d2', fontSize: 28 }} />
                  <Typography variant='h6' fontWeight={600}>
                    상태 정보
                  </Typography>
                </SectionHeader>

                <Stack spacing={3}>
                  {/* 상태 */}
                  <Box>
                    <InfoLabel>상태</InfoLabel>
                    <Box display='flex' alignItems='center' gap={1.5} mt={1}>
                      <CustomSwitch
                        value={isActiveStatus(userData?.status)}
                        onChangeToggle={handleStatusChange}
                        message={userStatusChangeMessage(userData?.status)}
                      />
                      <InfoValue>
                        {userStatusLabel(userData?.status)}
                      </InfoValue>
                    </Box>
                    {userData?.status === USER_STATUS.SECESSION && userData?.secessionOpinion && (
                      <Box mt={1.5}>
                        <InfoLabel>계정 잠금 사유</InfoLabel>
                        <InfoValue mt={1} sx={{ whiteSpace: 'pre-wrap', fontWeight: 500 }}>
                          {userData.secessionOpinion}
                        </InfoValue>
                      </Box>
                    )}
                  </Box>

                  <Divider />

                  {/* 가입일 */}
                  <Box>
                    <InfoLabel>가입일</InfoLabel>
                    <InfoValue mt={1}>
                      {formatDate(userData?.createdAt) || '-'}
                    </InfoValue>
                  </Box>

                  {/* 만료일 */}
                  <Box>
                    <InfoLabel>만료일</InfoLabel>
                    <InfoValue mt={1}>
                      {formatDate(userData?.expireDate) || '-'}
                    </InfoValue>
                  </Box>
                </Stack>
              </CardContent>
            </StyledCard>
          </Grid>

          {/* 그룹 정보 섹션 */}
          {groupData && (
            <Grid item xs={12}>
              <StyledCard>
                <CardContent sx={{ p: 3 }}>
                  <SectionHeader>
                    <GroupIcon sx={{ color: '#1976d2', fontSize: 28 }} />
                    <Typography variant='h5' fontWeight={600}>
                      그룹 정보
                    </Typography>
                  </SectionHeader>

                  <Grid container spacing={3}>
                    {/* 고객사명 */}
                    <Grid item xs={12} sm={6} md={3}>
                      <InfoLabel>고객사명</InfoLabel>
                      <InfoValue mt={1}>
                        {groupData.groupName || '-'}
                      </InfoValue>
                    </Grid>

                    {/* 그룹명 */}
                    <Grid item xs={12} sm={6} md={3}>
                      <InfoLabel>그룹명</InfoLabel>
                      <InfoValue mt={1}>
                        {groupData.groupCourse || '-'}  {groupData.groupCourseCount || 0}차수
                      </InfoValue>
                    </Grid>

                    {/* 그룹 상태 */}
                    <Grid item xs={12} sm={6} md={2}>
                      <InfoLabel>그룹 상태</InfoLabel>
                      <Box mt={1}>
                        <Chip
                          label={groupData.groupStatus === 2 ? '진행중' : groupData.groupStatus === 1 ? '대기' : '완료'}
                          color={groupData.groupStatus === 2 ? 'primary' : groupData.groupStatus === 1 ? 'warning' : 'success'}
                          size='small'
                          sx={{ fontWeight: 600 }}
                        />
                      </Box>
                    </Grid>

                    {/* 완료율 */}
                    <Grid item xs={12} sm={6} md={2}>
                      <InfoLabel>완료율</InfoLabel>
                      <InfoValue mt={1} sx={{ color: '#1976d2', fontWeight: 600 }}>
                        {groupData.groupCompletePercentage || 0}%
                      </InfoValue>
                    </Grid>

                    {/* 시작일 */}
                    <Grid item xs={12} sm={6} md={3}>
                      <InfoLabel>시작일</InfoLabel>
                      <InfoValue mt={1}>
                        {formatDate(groupData.groupStartDate) || '-'}
                      </InfoValue>
                    </Grid>

                    {/* 종료일 */}
                    <Grid item xs={12} sm={6} md={3}>
                      <InfoLabel>종료일</InfoLabel>
                      <InfoValue mt={1}>
                        {formatDate(groupData.groupDueDate) || '-'}
                      </InfoValue>
                    </Grid>

                    {/* 그룹 과정 목록 */}
                    {groupCourseList.length > 0 && (
                      <Grid item xs={12}>
                        <Divider sx={{ my: 2 }} />
                        <InfoLabel>그룹 과정 목록</InfoLabel>
                        <Stack direction='row' spacing={1} flexWrap='wrap' gap={1} mt={1.5}>
                          {groupCourseList.map((course, index) => (
                            <Chip
                              key={index}
                              label={course}
                              size='small'
                              variant='outlined'
                              color='primary'
                              sx={{ fontWeight: 500 }}
                            />
                          ))}
                        </Stack>
                      </Grid>
                    )}
                  </Grid>
                </CardContent>
              </StyledCard>
            </Grid>
          )}
        </Grid>
      </Stack>
    </Container>
  );
};

export default StudentDetailPage;
